import React from 'react';
import {
  FormPageInputItem,
  INPUT_ITEM_TYPE,
  ConfirmPropertyItemMailAddressAtSignSeparated,
  ConfirmPropertyItemMailAddressFree,
  ConfirmPropertyItemMailAddressDomainPulldown,
  ConfirmPropertyItemTextAdditionalText,
  ConfirmPropertyItemTextMultiLine,
  ConfirmPropertyItemTextName,
  ConfirmPropertyItemTextSingleLine,
  ConfirmPropertyItemTextTel,
  ConfirmPropertyItemTextZipCode,
  ConfirmPropertyItemTextZipCodeWithoutHyphen,
  DesignColors,
  FormPageProperty,
  ConfirmPropertyItemNumberAdditionalText,
  ConfirmPropertyItemDateFree,
  DateProperty,
  ConfirmPropertyItemDateYearPulldown,
  ConfirmPropertyItemMonthDayFree,
  ConfirmPropertyItemMonthDayPulldown,
  MonthDayProperty,
  ConfirmPropertyItemSingleSelectionPulldown,
  ConfirmPropertyItemSingleSelectionRadio,
  SingleSelectionProperty,
  ConfirmPropertyItemMultipleSelectionCheckbox,
  MultipleSelectionProperty,
  ConfirmPropertyItemMailRefusalFlagRadio,
  MailRefusalFlagProperty,
  TextProperty,
  ConfirmPropertyItemPasswordHashText,
  PROPERTY_TYPE,
} from 'shared';
import { FileUpload } from './FileUpload';
import { getSelectionLabelAndIndex } from './utils';

type ConfirmPropertyValueProps = {
  inputItem: FormPageInputItem;
  color: DesignColors['confirmPageInputFont'];
  property: FormPageProperty;
};

export const ConfirmPropertyValue: React.FC<ConfirmPropertyValueProps> = ({
  inputItem,
  color,
  property,
}) => {
  switch (inputItem.inputItemType) {
    case INPUT_ITEM_TYPE.mailAddressAtSignSeparated:
      return (
        <ConfirmPropertyItemMailAddressAtSignSeparated
          propertyId={property.propertyId}
          local={inputItem.local}
          domain={inputItem.domain}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.mailAddressDomainPulldown:
      return (
        <ConfirmPropertyItemMailAddressDomainPulldown
          propertyId={property.propertyId}
          local={inputItem.local}
          domain={inputItem.domain}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.mailAddressFree:
      return (
        <ConfirmPropertyItemMailAddressFree
          propertyId={property.propertyId}
          mailAddress={inputItem.mailAddress}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textAdditionalText:
      return (
        <ConfirmPropertyItemTextAdditionalText
          propertyId={property.propertyId}
          text={inputItem.text}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textMultiLine:
      return (
        <ConfirmPropertyItemTextMultiLine
          text={inputItem.text}
          propertyId={property.propertyId}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textName:
      return (
        <ConfirmPropertyItemTextName
          propertyId={property.propertyId}
          firstName={inputItem.firstName}
          lastName={inputItem.lastName}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textSingleLine: {
      const { propertyId, password } = property as TextProperty;
      return (
        <ConfirmPropertyItemTextSingleLine
          propertyId={propertyId}
          text={inputItem.text}
          color={color}
          password={password}
        />
      );
    }
    case INPUT_ITEM_TYPE.textTel:
      return (
        <ConfirmPropertyItemTextTel
          propertyId={property.propertyId}
          first={inputItem.first}
          second={inputItem.second}
          third={inputItem.third}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textZipCode:
      return (
        <ConfirmPropertyItemTextZipCode
          propertyId={property.propertyId}
          first={inputItem.first}
          second={inputItem.second}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.textZipCodeWithoutHyphen:
      return (
        <ConfirmPropertyItemTextZipCodeWithoutHyphen
          propertyId={property.propertyId}
          zipCode={inputItem.zipCode}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.numberAdditionalText:
      return (
        <ConfirmPropertyItemNumberAdditionalText
          propertyId={property.propertyId}
          value={inputItem.number}
          color={color}
        />
      );
    case INPUT_ITEM_TYPE.dateFree: {
      const { inputFormat } = property as DateProperty;
      return (
        <ConfirmPropertyItemDateFree
          propertyId={property.propertyId}
          year={inputItem.year}
          month={inputItem.month}
          day={inputItem.day}
          yearLabel={inputFormat.label.year}
          monthLabel={inputFormat.label.month}
          dayLabel={inputFormat.label.day}
          color={color}
        />
      );
    }
    case INPUT_ITEM_TYPE.dateYearPulldown: {
      const { inputFormat } = property as DateProperty;
      return (
        <ConfirmPropertyItemDateYearPulldown
          propertyId={property.propertyId}
          year={inputItem.year}
          month={inputItem.month}
          day={inputItem.day}
          yearLabel={inputFormat.label.year}
          monthLabel={inputFormat.label.month}
          dayLabel={inputFormat.label.day}
          color={color}
        />
      );
    }
    case INPUT_ITEM_TYPE.monthDayFree: {
      const { inputFormat } = property as MonthDayProperty;
      return (
        <ConfirmPropertyItemMonthDayFree
          propertyId={property.propertyId}
          month={inputItem.month}
          day={inputItem.day}
          monthLabel={inputFormat.label.month}
          dayLabel={inputFormat.label.day}
          color={color}
        />
      );
    }
    case INPUT_ITEM_TYPE.monthDayPulldown: {
      const { inputFormat } = property as MonthDayProperty;
      return (
        <ConfirmPropertyItemMonthDayPulldown
          propertyId={property.propertyId}
          month={inputItem.month}
          day={inputItem.day}
          monthLabel={inputFormat.label.month}
          dayLabel={inputFormat.label.day}
          color={color}
        />
      );
    }
    case INPUT_ITEM_TYPE.singleSelectionPulldown: {
      if (inputItem.value == null) {
        return (
          <ConfirmPropertyItemSingleSelectionPulldown
            propertyId={property.propertyId}
            color={color}
          />
        );
      }
      const { inputFormat, candidates, drillDown } =
        property as SingleSelectionProperty;
      const displayNumber = drillDown == null && inputFormat.isNumbering;
      const { index, label } = getSelectionLabelAndIndex({
        value: inputItem.value,
        candidates,
      });
      return (
        <ConfirmPropertyItemSingleSelectionPulldown
          propertyId={property.propertyId}
          color={color}
          label={label}
          index={displayNumber ? index : undefined}
        />
      );
    }
    case INPUT_ITEM_TYPE.singleSelectionRadio: {
      if (inputItem.value == null) {
        return (
          <ConfirmPropertyItemSingleSelectionRadio
            propertyId={property.propertyId}
            color={color}
          />
        );
      }
      const { inputFormat, candidates } = property as SingleSelectionProperty;
      const { index, label } = getSelectionLabelAndIndex({
        value: inputItem.value,
        candidates,
      });
      const subtext = inputItem.subtexts?.find(
        (item) => item.value === inputItem.value
      );
      return (
        <ConfirmPropertyItemSingleSelectionRadio
          propertyId={property.propertyId}
          color={color}
          label={label}
          index={inputFormat.isNumbering ? index : undefined}
          subtext={subtext?.subtext}
        />
      );
    }
    case INPUT_ITEM_TYPE.multipleSelectionCheckbox: {
      if (inputItem.values == null || inputItem.values.length < 1) {
        return (
          <ConfirmPropertyItemMultipleSelectionCheckbox
            propertyId={property.propertyId}
            color={color}
            values={[]}
          />
        );
      }
      const { inputFormat, candidates } = property as MultipleSelectionProperty;
      const values = [...(inputItem.values ?? [])]
        .sort((a, b) => a - b)
        .map((value) => {
          const { index, label } = getSelectionLabelAndIndex({
            value,
            candidates,
          });
          const subtext = inputItem.subtexts?.find(
            (item) => item.value === value
          );
          return {
            index: inputFormat.isNumbering ? index : undefined,
            label,
            subtext: subtext?.subtext,
          };
        });

      return (
        <ConfirmPropertyItemMultipleSelectionCheckbox
          propertyId={property.propertyId}
          color={color}
          values={values}
        />
      );
    }
    case INPUT_ITEM_TYPE.mailRefusalFlagRadio: {
      const { mailRefusalLabel, mailReceptionLabel } = (
        property as MailRefusalFlagProperty
      ).inputFormat;
      const label = inputItem.isRefused ? mailRefusalLabel : mailReceptionLabel;
      return (
        <ConfirmPropertyItemMailRefusalFlagRadio
          propertyId={property.propertyId}
          color={color}
          label={label}
        />
      );
    }
    case INPUT_ITEM_TYPE.passwordHashText: {
      return (
        <ConfirmPropertyItemPasswordHashText
          propertyId={property.propertyId}
          color={color}
        />
      );
    }
    case INPUT_ITEM_TYPE.fileUploadPiclib: {
      if (property.propertyType !== PROPERTY_TYPE.fileUpload) {
        return null;
      }
      return <FileUpload property={property} color={color} />;
    }
  }
};
